<template>
    <div class="app-container">
        <div class="background-splash">
            <font-awesome-icon :icon="['fa', 'dice-d20']" />
        </div>

        <div class="room-label">ROOM</div>
        <div class="room-key">{{ roomKey }}</div>

        <div v-if="!joined" class="join-room">
            <input type="text" v-model="name" placeholder="Your name" @keyup.enter="joinRoom" class="join-input" />
            <button @click="joinRoom" class="join-button" :disabled="loading || !name">
                <font-awesome-icon v-if="loading" :icon="['fa', 'dice-d20']" spin />
                Join
            </button>
        </div>
        <div v-else class="join-room">
            <div>
                <span>Connected as {{ name }}</span>
            </div>
            <button @click="leaveRoom(true)" class="join-button" :disabled="loading || !joined">
                <font-awesome-icon v-if="loading" :icon="['fa', 'dice-d20']" spin />
                Leave
            </button>
        </div>
    </div>
</template>

<script>
import track from "@/store/modules/track";
import { useToast } from "vue-toastification";

export default {
    setup() {
        // Get toast interface
        const toast = useToast();

        return { toast }
    },

    components: {
        //
    },

    data: () => ({
        joined: false,
        name: "",
        scene: {},
        loading: false,
        roomLimit: 2
    }),

    computed: {
        // Get room key from url
        roomKey() {
            return window.location.pathname.split('/').pop();
        }
    },

    mounted() {
        //
    },

    beforeUnmount() {
        //
    },

    methods: {
        joinRoom() {
            if (this.roomKey && this.name) {
                this.loading = true;

                axios.post('party/join', {
                    name: this.name,
                    room: this.roomKey
                }).then((response) => {
                    this.joined = true;
                    this.roomLimit = response.data.roomLimit;
                    Echo.join('party-room.' + this.roomKey)
                        .listen('.partySocket', ({ data }) => {
                            console.log("Package received", data);
                            // this.toast.success("Message received: " + JSON.stringify(data));
                            // this.name = data.name;
                            if (data.type === 'track-action') {
                                if (data.action === 'play') {
                                    // this.toast.success("Playing track");
                                    this.playTrack(data.track);
                                } else if (data.action === 'stop') {
                                    // this.toast.success("Stopping track");
                                    this.stopTrack(data.track);
                                } else if (data.action === 'pause') {
                                    // this.toast.success("Pausing track");
                                    this.pauseTrack(data.track);
                                } else if (data.action === 'volume') {
                                    this.setVolume(data.track);
                                }
                            }
                        })
                        .error(({ error }) => {
                            console.log("Error", error);
                            this.toast.error(error);
                        })
                        .here(users => {
                            console.log("Users currently in the channel:", users);
                        })
                        .joining(user => {
                            console.log("User joined:", user);
                        })
                        .leaving(user => {
                            console.log("User left:", user);

                            if (user.host) {
                                this.pauseAllTracks();
                            }
                        });
                }).catch((error) => {
                    console.error("Failed to join room", error);
                    if (error.status === 404) {
                        this.toast.error("Room not found");
                    } else {
                        this.toast.error("Failed to join room");
                    }
                }).finally(() => {
                    this.loading = false;
                });
            }

            return;
        },

        leaveRoom(force = false) {
            if (!this.joined) {
                return;
            }

            this.loading = true;

            axios.post('party/leave', {
                room: this.roomKey,
                name: this.name
            }).then(() => {
                this.joined = false;
                Echo.leave('party-room.' + this.roomKey);
                if (!force) {
                    this.toast.success("Left party room");
                }
                // Stop all tracks
                this.pauseAllTracks();
            }).catch((error) => {
                console.error("Failed to leave room", error);
                if (!force) {
                    this.toast.error("Failed to leave room");
                }
            }).finally(() => {
                this.loading = false;
            });

            return;
        },

        howlerInitiateTrack(track) {
            return new Promise((resolve, reject) => {
                // console.log('Initializing track', track);
                let trackSource = 'room/' + this.roomKey + '/track/' + track.source;

                if (track.album_id) {
                    trackSource = trackSource + '?library';
                }

                var audioTrack = new Howl({
                    src: [trackSource],
                    html5: true,
                    // preload: 'metadata',
                    loop: track.pivot.loop || false,
                    xhr: {
                        method: 'GET',
                        withCredentials: true
                    },

                    onplay: () => {
                        console.log('onplay', track);
                        track.fadeInTimer = track.pivot.fade_in_timer || 0;
                        track.fadeOutTimer = track.pivot.fade_out_timer || 0;

                        if ((track.fadeInTimer || track.fadeOutTimer) && !track.animationFrame) {
                            this.trackUpdateLoop(track);
                        }
                    },

                    onpause: () => {
                        console.log('onpause', track);
                        if (!!track.animationFrame) {
                            clearInterval(track.animationFrame);
                        }
                        track.fadeInProgress = false;
                    },

                    onstop: () => {
                        console.log('onstop', track);
                        if (!!track.animationFrame) {
                            clearInterval(track.animationFrame);
                        }
                        track.fadeInProgress = false;
                        track.currentTime = 0;
                        if (track.pivot) {
                            track.pivot.current_time = 0;
                        }
                    },

                    onend: () => {
                        console.log('onend', track);
                        if (!!track.animationFrame) {
                            clearInterval(track.animationFrame);
                        }
                        track.fadeInProgress = false;
                        track.currentTime = 0;
                        if (track.pivot) {
                            track.pivot.current_time = 0;
                        }
                    },
                });

                if (track.pivot && track.pivot.volume) {
                    audioTrack.volume(track.pivot?.volume / 100 || 1.0);
                } else {
                    audioTrack.volume(track.volume / 100 || 1.0);
                }

                if (track.pivot && track.pivot.current_time) {
                    // console.log('howlerInitTrack currentTime', track.pivot.current_time);
                    audioTrack.seek(track.pivot.current_time);
                    track.currentTime = track.pivot.current_time;
                }

                // audioTrack.volume(track.pivot?.volume / 100 || 1.0);
                // audioTrack.seek(track.pivot?.current_time || 0);
                track.audioTrack = audioTrack;

                // console.log('Track initialized', track);
                resolve(track);
            });
        },

        async getTrack(data) {
            let track = null;
            // console.log("Get track", data);
            if (!this.scene[data.pivot.scene_id]) {
                this.scene[data.pivot.scene_id] = [];

                track = await this.howlerInitiateTrack(data);
                this.scene[data.pivot.scene_id].push(track);
            } else {
                track = this.scene[data.pivot.scene_id].find(track => track.id === data.id);

                if (!track) {
                    track = await this.howlerInitiateTrack(data);
                    this.scene[data.pivot.scene_id].push(track);
                }
            }

            // console.log('Track retrieved', track);

            return track;
        },

        async playTrack(data) {
            // console.log("Play track", data);
            // Add track to scene if not already in scene
            let track = await this.getTrack(data);
            // console.log('got track:', track);

            // If we are playing a track, that is already playing, we do nothing
            if (track.audioTrack.playing()) {
                return;
            }

            console.log("Playing track:", track.id);
            if ((track.fadeInTimer || track.fadeOutTimer)) {
                if (track.animationFrame) {
                    clearInterval(track.animationFrame);
                }

                this.trackUpdateLoop(track);
            }

            console.log('play');
            track.audioTrack.play();

            return;
        },

        async stopTrack(data) {
            // console.log("Stop track", data);

            let track = await this.getTrack(data);

            if (track && track.audioTrack && track.audioTrack.playing()) {
                console.log("Stopping track:", track.id);
                track.audioTrack.stop();
            }

            return;
        },

        async pauseTrack(data) {
            // console.log("Pause track", data);

            let track = await this.getTrack(data);

            if (track && track.audioTrack && track.audioTrack.playing()) {
                console.log("Pausing track:", track);
                track.audioTrack.pause();
            }

            return;
        },

        // Pause all tracks that are playing
        pauseAllTracks() {
            console.log("Pausing all tracks");
            Object.values(this.scene).forEach(sceneTracks => {
                sceneTracks.forEach(track => {
                    if (track.audioTrack && track.audioTrack.playing()) {
                        console.log("Pausing track:", track.id);
                        track.audioTrack.pause();
                    }
                });
            });
        },

        trackUpdateLoop(track) {
            track.animationFrame = setInterval(() => {
                track.currentTime = track.audioTrack.seek() || 0; // Makes the progress bar work
                // console.debug('trackUpdateLoop', track.currentTime);

                if (track.fadeInTimer || track.fadeOutTimer) {
                    // If we are in the fade in period, run fade system
                    if (track.currentTime < track.fadeInTimer || track.currentTime > track.duration - track.fadeOutTimer) {
                        this.runFadeSystem(track);
                    } else {
                        // If we are not in the fade in or fade out period, reset fade progress
                        track.fadeInProgress = false;
                    }
                }

                // this.trackUpdateLoop(track);
            }, 100);
        },

        runFadeSystem(track) {
            if (track.fadeInProgress) {
                console.log('Fade in progress, skipping fade system');
                return;
            }

            // Get duration of track
            let duration = track.duration;

            // Get current time of track
            let currentTime = track.currentTime;
            console.log('runFadeSystem', currentTime, track.fadeInTimer, track.fadeOutTimer, duration);

            if (track.fadeInTimer || track.fadeOutTimer) {
                // If we are in the fade in period, run fade system
                if (track.fadeInTimer > 0) {
                    // If we are in the fade in period, run fade system
                    if (currentTime < track.fadeInTimer) {
                        track.fadeInProgress = true;

                        // Calculate fade in values, fadeInPercentage should be 0 if fadeIn just started
                        let fadeInPercentage = parseInt((currentTime / track.fadeInTimer) * 100);
                        // Calculate fade in duration left, in the beginning it should be the full fade in time, but decrease as time goes on
                        let fadeDurationLeft = (track.fadeInTimer - currentTime) * 1000;

                        // Figure what the volume should be, between 0 and set volume
                        let volume = parseFloat((track.pivot.volume / 100) * (fadeInPercentage / 100));

                        // console.log('fade in', volume, parseFloat(track.pivot.volume / 100), fadeDurationLeft, track.fadeInProgress, fadeInPercentage + '%');

                        // Set fade (from, to, duration)
                        track.audioTrack.fade(volume, parseFloat(track.pivot.volume / 100), fadeDurationLeft, track.howlerId);

                        // Set timer to remove fade in progress after fade in period
                        setTimeout(() => {
                            track.fadeInProgress = false;
                        }, fadeDurationLeft + 100);
                    }
                }

                if (track.fadeOutTimer > 0) {
                    // If we are in the fade out period, run fade system
                    if (currentTime > duration - track.fadeOutTimer) {
                        track.fadeInProgress = true;

                        // Calculate fade out values, fadeOutPercentage should be 0 if fadeOut just started
                        let fadeOutPercentage = parseInt(100 - (((duration - currentTime) / track.fadeOutTimer) * 100));
                        // Calculate fade out duration left, in the beginning it should be the full fade out time, but decrease as time goes on
                        let fadeDurationLeft = (track.fadeOutTimer * 1000) - ((currentTime - (duration - track.fadeOutTimer)) * 1000);

                        // Figure what the volume should be, between 0 and set volume
                        let volume = parseFloat(track.pivot.volume * ((100 - fadeOutPercentage) / 100) / 100);

                        // console.log('fade out', volume, 0, fadeDurationLeft, fadeOutPercentage);

                        // Set fade (from, to, duration)
                        track.audioTrack.fade(volume, 0, fadeDurationLeft, track.howlerId);

                        // Set timer to remove fade in progress after fade out period
                        setTimeout(() => {
                            track.fadeInProgress = false;
                        }, fadeDurationLeft + 100);
                    }
                }
            }
        },

        async setVolume(data) {
            let track = await this.getTrack(data);

            if (track && track.audioTrack) {
                // If volume is more than
                let volume = 0.5;

                if (data.pivot.volume >= 0 && data.pivot.volume <= 100) {
                    volume = parseFloat(parseInt(data.pivot.volume) / 100);
                }

                track.audioTrack.volume(volume);
            }
        }
    }
}
</script>

<style>
.main-content {
    overflow-x: hidden !important;
    overflow-y: hidden !important;
}
</style>

<style scoped>
.app-container {
    color: #000;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    flex-direction: column;
}

.room-label {
    font-family: monospace;
    font-size: 2rem;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
}

.room-key {
    font-family: monospace;
    font-weight: bold;
    font-size: 4rem;
    text-align: center;
    text-transform: uppercase;
}

.join-room {
    text-align: center;
}

.join-input,
.join-button {
    height: 40px;
    margin: 5px;
}

.join-input {
    padding: 0 10px;
    font-size: 1rem;
    border-radius: 4px;
    border-style: solid;
    border-width: 1px;
}

.join-button {
    padding: 0 20px;
    font-size: 1rem;
    cursor: pointer;
}

button:disabled {
    cursor: not-allowed;
    color: white;
    background-color: rgba(0, 0, 0, 0.5);
}
</style>
